import * as React from 'react';
import { useState, useEffect, useMemo, useCallback } from 'react';
import { Storage } from 'aws-amplify';
import { Upload, X, Loader2, Image as ImageIcon } from 'lucide-react';
import { cn } from '../../lib/utils';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Switch } from '../ui/switch';
import { FormField } from '../ui/form-field';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { generateRandomImageFileNameString } from '../../../application/common/Utilities';
import { useToast } from '../ui/toast';
import { FlexFieldConfig } from '../../hooks/use-deal-form';

// ── Types ───────────────────────────────────────────────────

interface FlexFieldsV2Props {
  flexFieldsConfig: FlexFieldConfig[];
  initialValues: Record<string, any>;
  submitted: boolean;
  currentDispositionID?: string;
  onFlexDataChange: (flexJSON: Record<string, any>) => void;
  onCanSaveChange: (canSave: boolean) => void;
}

interface FlexDataEntry {
  type: string;
  val: any;
  id: string;
  required: boolean;
}

// ── Utilities ───────────────────────────────────────────────

function setupFlexFields(
  fields: FlexFieldConfig[],
  values: Record<string, any>
): {
  flexData: Map<string, FlexDataEntry>;
  headersAndFields: Record<string, FlexFieldConfig[]>;
} {
  const ourValues = values || {};
  const flexData = new Map<string, FlexDataEntry>();

  for (const field of fields) {
    if (!field.displayField) continue;

    let value: any =
      field.defaultValue !== undefined ? field.defaultValue : field.default;

    if (field.name in ourValues) {
      value = ourValues[field.name];
    }

    if (field.type === 'Boolean' && value !== undefined && value !== null) {
      if (typeof value === 'string') {
        value = value.toLowerCase() === 'true' || value === '1';
      } else if (typeof value === 'number') {
        value = value === 1;
      } else {
        value = Boolean(value);
      }
    }

    const fieldIsRequired = field.required || false;

    if (field.type === 'List') {
      flexData.set(field.name, {
        type: field.type,
        val: value || '',
        id: value || '',
        required: fieldIsRequired,
      });
    } else if (field.type === 'ImageList') {
      flexData.set(field.name, {
        type: field.type,
        val: Array.isArray(value) ? value : [],
        id: field.name,
        required: fieldIsRequired,
      });
    } else if (field.type === 'Boolean') {
      flexData.set(field.name, {
        type: field.type,
        val: value || false,
        id: field.name,
        required: fieldIsRequired,
      });
    } else if (field.type === 'Contact') {
      flexData.set(field.name, {
        type: field.type,
        val: value && typeof value === 'object' ? value : {},
        id: field.name,
        required: fieldIsRequired,
      });
    } else {
      flexData.set(field.name, {
        type: field.type,
        val: value || '',
        id: field.name,
        required: fieldIsRequired,
      });
    }
  }

  // Group by heading
  const headersAndFields = fields
    .filter((f) => f.displayField)
    .reduce(
      (acc: Record<string, FlexFieldConfig[]>, el) => {
        if (!el.heading || el.heading === '') {
          acc.noHeaders.push(el);
        } else if (acc[el.heading]) {
          acc[el.heading].push(el);
        } else {
          acc[el.heading] = [el];
        }
        return acc;
      },
      { noHeaders: [] }
    );

  return { flexData, headersAndFields };
}

function getValidationConfig(
  flexFieldsConfig: FlexFieldConfig[]
): Record<string, any> {
  return flexFieldsConfig.reduce((acc: Record<string, any>, el) => {
    acc[el.name] = {
      required: el.required,
      requiredDispositions: (el as any).requiredDispositions,
      type: el.type,
    };
    if (el.type === 'String') {
      acc[el.name].minLength =
        el.minLength != null ? Number(el.minLength) : undefined;
      acc[el.name].maxLength =
        el.maxLength != null ? Number(el.maxLength) : undefined;
    }
    if (el.type === 'Number') {
      acc[el.name].minValue = el.minValue;
      acc[el.name].maxValue = el.maxValue;
    }
    if (el.type === 'Contact' && el.contactFields) {
      acc[el.name].contactFields = {};
      for (const fieldName in el.contactFields) {
        const fieldConfig = el.contactFields[fieldName];
        if (fieldConfig && fieldConfig.show) {
          acc[el.name].contactFields[fieldName] = {
            required:
              Object.prototype.hasOwnProperty.call(fieldConfig, 'required') &&
              fieldConfig.required === true,
            type: fieldConfig.type || 'String',
          };
        }
      }
    }
    if (el.type === 'ImageList') {
      acc[el.name].minRequired = el.minRequired;
      acc[el.name].minRequiredErrorText = el.minRequiredErrorText;
    }
    return acc;
  }, {});
}

function validateAllFields(
  flexData: Map<string, FlexDataEntry>,
  validationsConfig: Record<string, any>,
  currentDispositionID?: string
): {
  canSave: boolean;
  errors: Record<string, string>;
  flexJSON: Record<string, any>;
} {
  let canSave = true;
  const errors: Record<string, string> = {};
  const flexJSON: Record<string, any> = {};

  for (const [key, entry] of Array.from(flexData.entries())) {
    const { val, id, type } = entry;
    const validations = validationsConfig[key] || {};
    const _val = val === undefined ? '' : val;

    flexJSON[key] = type === 'List' ? id : _val;

    // Helper to check if value is present
    const hasValue = _val !== undefined && _val !== null && _val !== '';

    // Special handling for ImageList fields with minRequired
    const hasValidImageCount =
      type === 'ImageList' &&
      validations.minRequired !== undefined &&
      validations.minRequired !== null
        ? Array.isArray(_val) && _val.length >= validations.minRequired
        : hasValue;

    // Check required fields
    if (validations.required) {
      const requiredDispositions = validations.requiredDispositions || [];
      const isAlwaysRequired = requiredDispositions.length === 0;
      const isConditionallyRequired =
        requiredDispositions.includes(currentDispositionID);

      if (
        (isAlwaysRequired || isConditionallyRequired) &&
        !hasValidImageCount
      ) {
        canSave = false;
        if (
          type === 'ImageList' &&
          validations.minRequired !== undefined &&
          validations.minRequired !== null
        ) {
          const errorText =
            validations.minRequiredErrorText ||
            `At least ${validations.minRequired} image${
              validations.minRequired > 1 ? 's' : ''
            } required`;
          errors[key] = errorText;
        } else {
          errors[key] = 'This field is required';
        }
      }
    }

    // Contact sub-field validation
    if (
      type === 'Contact' &&
      typeof _val === 'object' &&
      validations.contactFields
    ) {
      for (const fieldName in validations.contactFields) {
        const fieldValidation = validations.contactFields[fieldName];
        if (!fieldValidation) continue;

        const fieldValue = _val[fieldName] || '';
        const hasFieldValue =
          fieldValue !== undefined && fieldValue !== null && fieldValue !== '';
        const errorKey = `${key}.${fieldName}`;

        // Check required contact fields
        if (fieldValidation.required && !hasFieldValue) {
          canSave = false;
          errors[errorKey] = 'This field is required';
        }

        // Format validation for contact fields
        if (hasFieldValue) {
          switch (fieldName) {
            case 'email': {
              if (
                !/^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.test(
                  fieldValue
                )
              ) {
                canSave = false;
                errors[errorKey] = 'Please enter a valid email address';
              }
              break;
            }
            case 'phoneNumber':
            case 'phoneNumber2': {
              const contactPhonePattern =
                /^(\+?1[-\s]?)?(\(?\d{3}\)?[-\s]?)?\d{3}[-\s]?\d{4}(\s+.*)?$/;
              if (!contactPhonePattern.test(fieldValue)) {
                canSave = false;
                errors[errorKey] = 'Please enter a valid phone number';
              }
              break;
            }
          }
        }
      }
    }

    // Field-specific validations (only validate if has value or required)
    if (hasValue || validations.required) {
      // maxLength
      if (
        validations.maxLength &&
        typeof _val === 'string' &&
        _val.length > Number(validations.maxLength)
      ) {
        canSave = false;
        errors[
          key
        ] = `${key} is limited to ${validations.maxLength} characters`;
      }
      // minLength
      if (
        validations.minLength &&
        typeof _val === 'string' &&
        _val.length < Number(validations.minLength)
      ) {
        canSave = false;
        errors[
          key
        ] = `${key} must be at least ${validations.minLength} characters`;
      }
      // minValue
      if (
        validations.minValue &&
        hasValue &&
        parseFloat(_val) < parseFloat(validations.minValue)
      ) {
        canSave = false;
        errors[key] = `${key} must be ${validations.minValue} or greater`;
      }
      // maxValue
      if (
        validations.maxValue &&
        hasValue &&
        parseFloat(_val) > parseFloat(validations.maxValue)
      ) {
        canSave = false;
        errors[key] = `${key} must be ${validations.maxValue} or less`;
      }

      // Type-specific format validation
      if (hasValue && typeof _val === 'string') {
        switch (type) {
          case 'Phone': {
            const phonePattern =
              /^(\+?1[-\s]?)?(\(?\d{3}\)?[-\s]?)?\d{3}[-\s]?\d{4}(\s+.*)?$/;
            if (!phonePattern.test(_val)) {
              canSave = false;
              errors[key] = 'Please enter a valid phone number';
            }
            break;
          }
          case 'Number': {
            if (isNaN(Number(_val))) {
              canSave = false;
              errors[key] = 'Please enter only numbers';
            }
            break;
          }
          case 'Date': {
            if (
              !/^(0[1-9]|1[0-2]|[1-9])\/(0[1-9]|1\d|2\d|3[01]|[1-9])\/(19|20)\d{2}$/.test(
                _val
              )
            ) {
              canSave = false;
              errors[key] = 'Please enter a date (ex: 1/2/2022)';
            }
            break;
          }
          case 'Money': {
            if (
              !/^(?=.*?\d)\$?(([1-9]\d{0,2}(,\d{3})*)|\d+)?(\.\d{1,2})?$/.test(
                _val
              )
            ) {
              canSave = false;
              errors[key] = 'Please enter a valid dollar amount';
            }
            break;
          }
          case 'Email': {
            if (
              !/^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.test(
                _val
              )
            ) {
              canSave = false;
              errors[key] = 'Please enter a valid email address';
            }
            break;
          }
        }
      }
    }
  }

  return { canSave, errors, flexJSON };
}

// ── Main Component ──────────────────────────────────────────

export function FlexFieldsV2({
  flexFieldsConfig,
  initialValues,
  submitted,
  currentDispositionID,
  onFlexDataChange,
  onCanSaveChange,
}: FlexFieldsV2Props) {
  const [flexData, setFlexData] = useState<Map<string, FlexDataEntry>>(
    new Map()
  );
  const [headersAndFields, setHeadersAndFields] = useState<
    Record<string, FlexFieldConfig[]>
  >({});
  const [_errors, setErrors] = useState<Record<string, string>>({});
  const [initialized, setInitialized] = useState(false);

  // Build validation config from flex field definitions (same as mobile getValidationConfig)
  const validationsConfig = useMemo(() => {
    return getValidationConfig(flexFieldsConfig);
  }, [flexFieldsConfig]);

  // Setup flex fields when config or initial values change
  useEffect(() => {
    if (!flexFieldsConfig.length) return;

    const { flexData: newFlexData, headersAndFields: newHeaders } =
      setupFlexFields(flexFieldsConfig, initialValues);
    setFlexData(newFlexData);
    setHeadersAndFields(newHeaders);
    setInitialized(true);

    // Run initial validation and emit flex JSON
    const {
      canSave,
      errors: newErrors,
      flexJSON,
    } = validateAllFields(newFlexData, validationsConfig, currentDispositionID);
    onCanSaveChange(canSave);
    onFlexDataChange(flexJSON);
    if (submitted) {
      setErrors(newErrors);
    }
  }, [flexFieldsConfig, initialValues]);

  // Update field value
  const updateField = useCallback(
    (fieldName: string, value: any, id?: string) => {
      setFlexData((prev) => {
        const newMap = new Map(prev);
        const existing = newMap.get(fieldName);
        if (!existing) return prev;

        newMap.set(fieldName, {
          ...existing,
          val: value,
          id: id !== undefined ? id : existing.id,
        });

        // Validate and emit
        const {
          canSave,
          errors: newErrors,
          flexJSON,
        } = validateAllFields(newMap, validationsConfig, currentDispositionID);
        onCanSaveChange(canSave);
        onFlexDataChange(flexJSON);
        if (submitted) {
          setErrors(newErrors);
        }

        return newMap;
      });
    },
    [
      submitted,
      onCanSaveChange,
      onFlexDataChange,
      validationsConfig,
      currentDispositionID,
    ]
  );

  // Re-validate when submitted changes (show errors on first submit attempt)
  useEffect(() => {
    if (submitted && initialized) {
      const { canSave, errors: newErrors } = validateAllFields(
        flexData,
        validationsConfig,
        currentDispositionID
      );
      onCanSaveChange(canSave);
      setErrors(newErrors);
    }
  }, [submitted]);

  // Compute display errors synchronously so they show immediately when submitted becomes true
  const displayErrors = useMemo(() => {
    if (!submitted || !initialized) return {};
    const { errors: currentErrors } = validateAllFields(
      flexData,
      validationsConfig,
      currentDispositionID
    );
    return currentErrors;
  }, [
    submitted,
    initialized,
    flexData,
    validationsConfig,
    currentDispositionID,
  ]);

  if (!initialized || !Object.keys(headersAndFields).length) {
    return null;
  }

  const sections = [
    ...Object.entries(headersAndFields).filter(([key]) => key !== 'noHeaders'),
    ['', headersAndFields.noHeaders || []],
  ].filter(([, fields]) => (fields as FlexFieldConfig[]).length > 0);

  return (
    <div className="space-y-6">
      {sections.map(([heading, fields], sectionIndex) => (
        <div
          key={sectionIndex}
          className="rounded-xl border border-border bg-card overflow-hidden"
        >
          {heading && (
            <div className="px-5 py-4 border-b border-border bg-muted/50">
              <h3 className="text-base font-semibold text-foreground capitalize">
                {heading as string}
              </h3>
            </div>
          )}
          <div className="p-5 space-y-4">
            {(fields as FlexFieldConfig[]).map((field) => (
              <FlexFieldInput
                key={field.name}
                field={field}
                value={flexData.get(field.name)?.val}
                id={flexData.get(field.name)?.id}
                error={displayErrors[field.name]}
                allErrors={displayErrors}
                onChange={(val, id) => updateField(field.name, val, id)}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Individual Field Input ──────────────────────────────────

interface FlexFieldInputProps {
  field: FlexFieldConfig;
  value: any;
  id?: string;
  error?: string;
  allErrors: Record<string, string>;
  onChange: (value: any, id?: string) => void;
}

function FlexFieldInput({
  field,
  value,
  id,
  error,
  allErrors,
  onChange,
}: FlexFieldInputProps) {
  const label = field.required ? `${field.label} *` : field.label;

  // Text-based fields
  if (
    ['String', 'Notes', 'Email', 'Number', 'Money', 'Date', 'Phone'].includes(
      field.type
    )
  ) {
    const maxLength = getMaxLength(field);
    const isMultiline = field.type === 'Notes';
    const inputType = getInputType(field.type);

    return (
      <FormField label={label} error={error}>
        {isMultiline ? (
          <Textarea
            placeholder={field.label}
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            maxLength={maxLength}
            className={cn(error && 'border-destructive')}
            rows={4}
          />
        ) : (
          <Input
            type={inputType}
            placeholder={field.label}
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            maxLength={maxLength}
            className={cn(error && 'border-destructive')}
          />
        )}
      </FormField>
    );
  }

  // Boolean field
  if (field.type === 'Boolean') {
    return (
      <div className="flex items-center justify-between py-2">
        {!field.hideLabel && (
          <span className="text-sm font-semibold text-foreground">{label}</span>
        )}
        <Switch
          checked={!!value}
          onCheckedChange={(checked) => onChange(checked)}
        />
      </div>
    );
  }

  // List/Select field
  if (field.type === 'List') {
    const options = field.values || [];
    const hasNoSelection = !field.required;

    return (
      <FormField label={label} error={error}>
        <Select
          value={id || ''}
          onValueChange={(newId) => {
            const selected = options.find((o) => o.id === newId);
            onChange(selected?.label || newId, newId);
          }}
        >
          <SelectTrigger className={cn(error && 'border-destructive')}>
            <SelectValue placeholder={`Select ${field.label}`} />
          </SelectTrigger>
          <SelectContent>
            {hasNoSelection && (
              <SelectItem value="__none__">No Selection</SelectItem>
            )}
            {options.map((option) => (
              <SelectItem key={option.id} value={option.id}>
                {option.label || option.id}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </FormField>
    );
  }

  // ImageList field
  if (field.type === 'ImageList') {
    return (
      <ImageListField
        field={field}
        value={value}
        error={error}
        label={label}
        onChange={onChange}
      />
    );
  }

  // Contact field
  if (field.type === 'Contact') {
    return (
      <ContactField
        field={field}
        value={value}
        error={error}
        allErrors={allErrors}
        label={label}
        onChange={onChange}
      />
    );
  }

  // Read Only - skip
  if (field.type === 'Read Only') {
    return null;
  }

  // Fallback for unknown types
  return (
    <FormField label={label} error={error}>
      <Input
        placeholder={field.label}
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        className={cn(error && 'border-destructive')}
      />
    </FormField>
  );
}

// ── ImageList Field ─────────────────────────────────────────

function ImageListField({
  field,
  value,
  error,
  label,
  onChange,
}: {
  field: FlexFieldConfig;
  value: any;
  error?: string;
  label: string;
  onChange: (value: any) => void;
}) {
  const toast = useToast();
  const [isUploading, setIsUploading] = useState(false);
  const [imageUrls, setImageUrls] = useState<Record<string, string>>({});
  const images: string[] = Array.isArray(value) ? value : [];

  // Fetch signed URLs for existing images
  useEffect(() => {
    let cancelled = false;

    async function fetchUrls() {
      const newUrls: Record<string, string> = {};
      for (const key of images) {
        if (!key || imageUrls[key]) continue;
        try {
          const url = await Storage.get(key);
          if (!cancelled) {
            newUrls[key] = url as string;
          }
        } catch {
          // Skip failed fetches
        }
      }
      if (!cancelled && Object.keys(newUrls).length > 0) {
        setImageUrls((prev) => ({ ...prev, ...newUrls }));
      }
    }

    if (images.length > 0) {
      fetchUrls();
    }

    return () => {
      cancelled = true;
    };
  }, [images.join(',')]);

  const handleDrop = async (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) {
      await uploadFile(file);
    }
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      await uploadFile(file);
    }
    e.target.value = '';
  };

  const uploadFile = async (file: File) => {
    const formats = ['jpeg', 'jpg', 'png', 'gif', 'mp4', 'mov', 'avi', 'mkv'];
    const ext = file.name.split('.').pop()?.toLowerCase() || '';
    if (!formats.includes(ext)) {
      toast.error(
        'Unsupported file format',
        'Supported: png, gif, jpeg, mp4, mov'
      );
      return;
    }

    setIsUploading(true);
    try {
      const randomString = generateRandomImageFileNameString();
      const newFileName = `${randomString}.${ext}`;
      const uploaded = await Storage.put(newFileName, file, {
        contentType: file.type,
      });
      const newImages = [...images, uploaded.key];
      onChange(newImages);
      toast.success('File uploaded successfully');
    } catch (err) {
      toast.error('Upload failed', 'Please try again');
    }
    setIsUploading(false);
  };

  const handleDelete = (index: number) => {
    const updated = [...images];
    updated.splice(index, 1);
    onChange(updated);
  };

  const isVideoFile = (key: string): boolean => {
    return /\.(mp4|mov|avi|mkv)$/i.test(key);
  };

  return (
    <FormField label={label} error={error}>
      <div className="space-y-3">
        {images.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {images.map((img, i) => (
              <div key={i} className="relative group">
                <div className="w-20 h-20 rounded-lg border border-border bg-muted flex items-center justify-center overflow-hidden">
                  {imageUrls[img] && !isVideoFile(img) ? (
                    <img
                      src={imageUrls[img]}
                      alt={`${field.label} ${i + 1}`}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <ImageIcon className="size-6 text-muted-foreground" />
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => handleDelete(i)}
                  className="absolute -top-2 -right-2 size-5 rounded-full bg-destructive text-destructive-foreground flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <X className="size-3" />
                </button>
              </div>
            ))}
          </div>
        )}
        <div
          onDragOver={(e) => e.preventDefault()}
          onDrop={handleDrop}
          className={cn(
            'border-2 border-dashed border-border rounded-lg p-6 text-center cursor-pointer hover:border-primary/50 transition-colors',
            error && 'border-destructive'
          )}
          onClick={() =>
            document.getElementById(`file-input-${field.name}`)?.click()
          }
        >
          {isUploading ? (
            <Loader2 className="size-6 animate-spin mx-auto text-muted-foreground" />
          ) : (
            <div className="flex flex-col items-center gap-2">
              <Upload className="size-6 text-muted-foreground" />
              <span className="text-sm text-muted-foreground">
                Drop files here or click to upload
              </span>
            </div>
          )}
        </div>
        <input
          id={`file-input-${field.name}`}
          type="file"
          accept="image/*,video/*"
          className="hidden"
          onChange={handleFileSelect}
        />
      </div>
    </FormField>
  );
}

// ── Contact Field ───────────────────────────────────────────

function ContactField({
  field,
  value,
  error,
  allErrors,
  label,
  onChange,
}: {
  field: FlexFieldConfig;
  value: any;
  error?: string;
  allErrors: Record<string, string>;
  label: string;
  onChange: (value: any) => void;
}) {
  const contactData = value && typeof value === 'object' ? value : {};
  const contactFields = field.contactFields || {};

  const visibleFields = Object.entries(contactFields).filter(
    ([, config]: [string, any]) => config && config.show
  );

  const updateContactField = (fieldName: string, fieldValue: string) => {
    const updated = { ...contactData, [fieldName]: fieldValue };
    onChange(updated);
  };

  const getContactFieldLabel = (fieldName: string): string => {
    const labels: Record<string, string> = {
      firstName: 'First Name',
      lastName: 'Last Name',
      email: 'Email',
      phoneNumber: 'Phone Number',
      phoneNumber2: 'Phone Number 2',
      address: 'Address',
      city: 'City',
      state: 'State',
      zip: 'Zip Code',
    };
    return labels[fieldName] || fieldName;
  };

  return (
    <div className="space-y-3">
      <span className="text-sm font-semibold text-foreground pl-1">
        {label}
      </span>
      {error && <p className="text-xs text-destructive pl-1">{error}</p>}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pl-2 border-l-2 border-primary/20">
        {visibleFields.map(([fieldName, config]: [string, any]) => {
          const fieldLabel = getContactFieldLabel(fieldName);
          const isRequired = config.required;
          const subFieldError = allErrors[`${field.name}.${fieldName}`];

          return (
            <FormField
              key={fieldName}
              label={isRequired ? `${fieldLabel} *` : fieldLabel}
              error={subFieldError}
            >
              <Input
                placeholder={fieldLabel}
                value={contactData[fieldName] || ''}
                onChange={(e) => updateContactField(fieldName, e.target.value)}
                className={cn(subFieldError && 'border-destructive')}
              />
            </FormField>
          );
        })}
      </div>
    </div>
  );
}

// ── Helpers ─────────────────────────────────────────────────

function getMaxLength(field: FlexFieldConfig): number {
  switch (field.type) {
    case 'String':
      return field.maxLength || 80;
    case 'Notes':
      return field.maxLength || 4096;
    case 'Email':
    case 'Date':
      return 255;
    case 'Number':
    case 'Money':
    case 'Phone':
      return 20;
    default:
      return 4096;
  }
}

function getInputType(fieldType: string): string {
  switch (fieldType) {
    case 'Email':
      return 'email';
    case 'Phone':
      return 'tel';
    case 'Number':
    case 'Money':
      return 'text'; // Keep as text to allow custom formatting
    default:
      return 'text';
  }
}
